import React, { useState, useRef, useEffect } from 'react';
import styles from './NotificationBell.module.css';
import { BASE_URL } from '../utils/config';
import { authFetch } from '../utils/authFetch';
import { toast } from 'react-toastify';

const NotificationBell = () => {
  const [showNotifications, setShowNotifications] = useState(false);
  const [notifications, setNotifications] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [unreadCount, setUnreadCount] = useState(0);
  const notificationRef = useRef(null);

  // Function to strip HTML tags from text
  const stripHtmlTags = (html) => {
    if (!html) return '';
    const tmp = document.createElement('DIV');
    tmp.innerHTML = html;
    return tmp.textContent || tmp.innerText || '';
  };

  // Function to format large numbers for badge display
  const formatBadgeCount = (count) => {
    if (count <= 0) return '0';
    if (count < 1000) return count.toString();
    if (count < 10000) {
      // For 1000-9999, show as "9.9k" (one decimal place)
      const thousands = (count / 1000).toFixed(1);
      // Remove trailing zero if it's a whole number (e.g., "2.0k" -> "2k")
      return `${parseFloat(thousands)}k`;
    }
    // For 10000-99999, show as "10k+" to "99k+"
    if (count < 100000) {
      const thousands = Math.floor(count / 1000);
      return `${thousands}k+`;
    }
    // For 100000+, cap at "99k+" to keep badge compact
    return '99k+';
  };

  const fetchNotifications = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await authFetch(`${BASE_URL}/notifications/all`);
      
      if (!response.ok) {
        throw new Error('Failed to fetch notifications');
      }

      const data = await response.json();
      console.log(">>>>>>>>>>>>data", data.data);
      setNotifications(data.data || []);
      // Count unread notifications (read === false or read is undefined/null)
      const unreadNotifications = (data.data || []).filter(n => !n.read || n.read === false);
      setUnreadCount(unreadNotifications.length);
    } catch (err) {
      console.error('Error fetching notifications:', err);
      setError('Failed to load notifications');
      toast.error('Failed to load notifications');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchNotifications();
    
    // Set up polling for new notifications every minute
    const pollInterval = setInterval(fetchNotifications, 60000);

    // Click outside handler
    const handleClickOutside = (event) => {
      if (notificationRef.current && !notificationRef.current.contains(event.target)) {
        setShowNotifications(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      clearInterval(pollInterval);
    };
  }, []);

  const handleNotificationClick = () => {
    setShowNotifications(!showNotifications);
  };

  const markAsRead = async (id) => {
    try {
      const response = await authFetch(`${BASE_URL}/notifications/${id}/read`, {
        method: 'PUT'
      });

      if (!response.ok) {
        throw new Error('Failed to mark notification as read');
      }

      // Update local state
      const updatedNotifications = notifications.map(notification => 
        notification.id === id ? { ...notification, read: true } : notification
      );
      setNotifications(updatedNotifications);
      // Recalculate unread count
      const unreadCount = updatedNotifications.filter(n => !n.read || n.read === false).length;
      setUnreadCount(unreadCount);
    } catch (err) {
      console.error('Error marking notification as read:', err);
      toast.error('Failed to mark notification as read');
    }
  };

  const markAllAsRead = async () => {
    try {
      const response = await authFetch(`${BASE_URL}/notifications/mark-all-read`, {
        method: 'PUT'
      });

      if (!response.ok) {
        throw new Error('Failed to mark all notifications as read');
      }

      // Update local state
      setNotifications(notifications.map(n => ({ ...n, read: true })));
      setUnreadCount(0);
    } catch (err) {
      console.error('Error marking all notifications as read:', err);
      toast.error('Failed to mark all notifications as read');
    }
  };

  return (
    <div className={styles.notificationContainer} ref={notificationRef}>
      <button className={styles.bellButton} onClick={handleNotificationClick}>
        <svg 
          width="24" 
          height="24" 
          viewBox="0 0 24 24" 
          fill="none" 
          stroke="currentColor" 
          strokeWidth="2" 
          strokeLinecap="round" 
          strokeLinejoin="round"
        >
          <path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9"></path>
          <path d="M13.73 21a2 2 0 0 1-3.46 0"></path>
        </svg>
        {unreadCount > 0 && (
          <span 
            className={`${styles.badge} ${
              unreadCount < 10 ? styles.badgeSingle : 
              unreadCount < 100 ? styles.badgeDouble : 
              styles.badgeFormatted
            }`}
          >
            {formatBadgeCount(unreadCount)}
          </span>
        )}
      </button>

      {showNotifications && (
        <div className={styles.notificationDropdown}>
          <div className={styles.notificationHeader}>
            <h3>Notifications</h3>
            {unreadCount > 0 && (
              <button 
                className={styles.markAllRead}
                onClick={markAllAsRead}
              >
                Mark all as read
              </button>
            )}
          </div>
          <div className={styles.notificationList}>
            {loading ? (
              <div className={styles.loadingState}>Loading notifications...</div>
            ) : error ? (
              <div className={styles.errorState}>{error}</div>
            ) : notifications.length > 0 ? (
              notifications.map(notification => (
                <div 
                  key={notification.id} 
                  className={`${styles.notificationItem} ${!notification.read ? styles.unread : ''}`}
                  onClick={() => markAsRead(notification.id)}
                >
                  <div className={styles.notificationContent}>
                    {!notification.read && (
                      <span className={styles.unreadDot}></span>
                    )}
                    <div className={styles.notificationText}>
                      <h4>{stripHtmlTags(notification.title)}</h4>
                      <p>{stripHtmlTags(notification.body)}</p>
                      <span className={styles.notificationTime}>
                        {new Date(notification.createdAt).toLocaleString()}
                      </span>
                    </div>
                  </div>
                </div>
              ))
            ) : (
              <div className={styles.noNotifications}>
                No notifications
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default NotificationBell;
